import { useMutation, useQueryClient } from '@tanstack/react-query';
import { downloadFileFromBlob } from 'helpers/util.helpers';
import {
  UpdateSimNameParams,
  UpdateSimTagsParams,
  EnableWifiCallingParams,
} from 'lib/api/requests/sim/sim.requests.types';
import { useYourSimsContext } from 'views/YourSiMs/hooks';
import iotApi from '../../api/iotApi.client';
import { SimQueryKey } from './sim.hooks.keys';

export function useChangePhoneNumber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      simId: number;
      zipcode: string;
      ultraUserId: string;
    }) => {
      const res = await iotApi.sim.changePhoneNumber(
        {
          id: data.simId,
          zipcode: data.zipcode,
        },
        data.ultraUserId
      );

      return res;
    },
    onSuccess: () => {
      // TODO: need to add ICCID to the queryKey to only invalidate the specific sim instead of all sims
      queryClient.invalidateQueries([SimQueryKey.SIM_CARD_DETAIL]);
    },
  });
}

export function useUpdateSimName() {
  const queryClient = useQueryClient();
  const { getFilteredSimsParams } = useYourSimsContext();

  return useMutation({
    mutationFn: async (data: UpdateSimNameParams) => {
      const res = await iotApi.sim.updateSimName(data);

      return res;
    },
    onSettled: () => {
      queryClient.invalidateQueries([
        SimQueryKey.FILTERED_SIMS,
        JSON.stringify(getFilteredSimsParams()),
      ]);
    },
  });
}

export function useUpdateSimTags() {
  const queryClient = useQueryClient();
  const { getFilteredSimsParams } = useYourSimsContext();

  return useMutation({
    mutationFn: async (data: UpdateSimTagsParams) => {
      const res = await iotApi.sim.updateSimTags(data);

      return res;
    },
    onSettled: () => {
      queryClient.invalidateQueries([
        SimQueryKey.FILTERED_SIMS,
        JSON.stringify(getFilteredSimsParams()),
      ]);
    },
  });
}

export function useDeleteSims() {
  const queryClient = useQueryClient();
  const { getFilteredSimsParams } = useYourSimsContext();

  return useMutation({
    mutationFn: async (data: { ids: number[] }) => {
      const res = await iotApi.sim.deleteSims(data);

      return res;
    },
    onSettled: () => {
      queryClient.invalidateQueries([
        SimQueryKey.FILTERED_SIMS,
        JSON.stringify(getFilteredSimsParams()),
      ]);
    },
  });
}

export function useGeneratePDF() {
  return useMutation({
    mutationFn: async (data: { ICCID: string }) => {
      const res = await iotApi.sim.generatePDF(data);

      return res;
    },
  });
}

export function useDownloadEsimPDF() {
  return useMutation({
    mutationFn: async (data: { ICCID: string }) => {
      const res = await iotApi.sim.downloadEsimPDF(data);

      return res;
    },
  });
}

/**
 * Custom mutation hook to download the PDF of an eSIM
 *
 * @description
 * This hook uses the `useGeneratePDF` and `useDownloadEsimPDF` hooks to download the PDF of an eSIM.
 * It first attempts to download the PDF using the `useDownloadEsimPDF` hook, and if that fails, it generates the PDF using the `useGeneratePDF` hook.
 * The hook returns a boolean indicating whether the PDF was successfully downloaded or generated.
 *
 * @returns A boolean indicating whether the PDF was successfully downloaded or generated.
 */
export function usePDFDownload() {
  const generatePDFMutation = useGeneratePDF();
  const downloadPDFMutation = useDownloadEsimPDF();

  return useMutation({
    mutationFn: async (ICCID: string) => {
      try {
        const pdfBlob = await downloadPDFMutation.mutateAsync({ ICCID });

        downloadFileFromBlob(pdfBlob, ICCID);

        return true;
      } catch (e) {
        await generatePDFMutation.mutateAsync({ ICCID });

        return false;
      }
    },
  });
}

export function useEnableWifiCalling() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: EnableWifiCallingParams) => {
      const res = await iotApi.sim.enableWifiCalling(data);

      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([SimQueryKey.SIM_CARD_DETAIL]);
    },
  });
}
